/**
 * Recovery from a server socket REFUSAL — the one disconnect socket.io will
 * not retry. The server sheds load with `connection:refused` +
 * `disconnect(true)` ("io server disconnect"), which socket.io-client NEVER
 * auto-reconnects from; without this module a refused client stays without
 * realtime until a layout remount, token change, or restart.
 * docs/architecture/realtime-refusal-recovery.md is the spec of record.
 *
 * Invariants, each of which shaped the implementation:
 *
 * - SUCCESS IS NEVER DECLARED ON `connect`. Socket.IO acks the namespace
 *   BEFORE the admission check runs, so every refusal cycle is `connect` →
 *   `connection:refused` → `disconnect` (pinned by the gateway spec). Success
 *   = every socket of the domain connected AND RECOVERY_SETTLE_MS without a
 *   refusal; a refusal during the settle cancels it, the attempt counter
 *   keeps counting. Treating `connect` as success made retries infinite.
 * - SCOPED PER CONNECTION DOMAIN (the Manager), not app-global: verified on
 *   socket.io-client 4.8.3, `io()` never deletes a namespace from a Manager's
 *   `nsps`, so recreating a socket for a namespace the cached Manager EVER
 *   held (chat remounts, token-rotation) silently mints a NEW Manager and
 *   connection. Each domain has its own budget, timer, settle and sockets —
 *   two refused Managers must not eat each other's attempts.
 * - THE COORDINATOR RESYNCS NOTHING. It calls `connect()` on the same
 *   registered socket instances; each socket's own connect handler does its
 *   catch-up (chat re-joins + re-reads; events/cue go through the resync
 *   gate).
 * - ONLY A REFUSAL IS RECOVERED, classified PER SOCKET (a sibling's connect
 *   ack must not erase another namespace's refusal). Auth failures would loop
 *   on a bad token; transport drops are socket.io's own retry with the finite
 *   budget in socket-options.ts — and when THAT budget is spent, foreground
 *   (`resume`) and a successful HTTP response (`nudge`, throttled) rearm it,
 *   because an exhausted Manager never reconnects on its own.
 * - EVERY SCHEDULE IS BOUNDED (AGENTS.md): pressure/capacity gets five
 *   attempts over ~7.75 min — sized to cover the scale-out alarm (3x60 s
 *   datapoints) plus replacement-task start (~2 min), half-to-full jittered
 *   so refused clients do not return in unison. The per-user cap gets two
 *   widely spaced attempts, NOT never-retry: the count clears as dead sockets
 *   are detected and the balancer may land elsewhere. Exhaustion parks the
 *   domain until a lifecycle trigger; background pauses timers, foreground
 *   resumes them; a token change rebuilds sockets and cancels everything.
 */

/** Mirror of the server's REFUSED_EVENT (connection-admission.ts). The
 * cross-surface contract test (tests/contracts/) fails if either side drifts. */
export const REFUSED_EVENT = "connection:refused";

/** Mirror of the server's READY_EVENT — emitted only after EVERY admission
 * check passed. The bare `connect` ack precedes admission and proves nothing:
 * on a shed it is immediately followed by a refusal, so heavy client work
 * (HTTP resync, chat catch-up, outbox flush) keys off THIS event, or each
 * recovery attempt aims a refresh herd at a server refusing for load. Same
 * contract test pins it. */
export const READY_EVENT = "connection:ready";

/** Mirror of the server's `Refusal` union, as runtime values so the contract
 * test can compare them. Unknown reasons are treated as pressure. */
export const REFUSAL_REASONS = [
  "refused_per_user_cap",
  "refused_task_capacity",
  "refused_task_pressure",
] as const;
export type RefusalReason = (typeof REFUSAL_REASONS)[number];

/** Attempt delays, before jitter. Bounded by construction — see the header. */
export const PRESSURE_RETRY_DELAYS_MS = [
  15_000, 30_000, 60_000, 120_000, 240_000,
] as const;
export const PER_USER_RETRY_DELAYS_MS = [60_000, 300_000] as const;

/** Delay before the single attempt a foreground resume grants. */
export const RESUME_RETRY_DELAY_MS = 2_000;

/**
 * How long a domain's full socket set must stay connected, refusal-free,
 * before recovery declares success and resets its attempt counter. The
 * refusal arrives one round trip after the connect ack (the server admits or
 * refuses inside handleConnection), so ten seconds is generous — and the cost
 * of over-waiting is nil, while resetting early recreates the infinite
 * shortest-delay loop this window exists to prevent.
 */
export const RECOVERY_SETTLE_MS = 10_000;

/** Floor between HTTP-success nudges. A nudge is a no-op when everything is
 * connected, so this only bounds how often a genuinely dead socket gets a
 * kick while HTTP traffic flows. */
export const NUDGE_MIN_INTERVAL_MS = 30_000;

/** The slice of a socket.io Socket this module touches. Structural (method
 * syntax, so socket.io's stricter `on` overloads stay assignable), so tests
 * need no socket.io and this file imports nothing. `io` is the Manager —
 * sockets sharing one share a connection, and therefore recovery state; a
 * socket without one is its own domain. */
export type RecoverableSocket = {
  disconnected: boolean;
  connect(): unknown;
  on(event: string, cb: (...args: unknown[]) => void): unknown;
  io?: object;
};

type Deps = {
  setTimer: (cb: () => void, ms: number) => unknown;
  clearTimer: (handle: unknown) => void;
  random: () => number;
  now: () => number;
};

type Domain = {
  sockets: Set<RecoverableSocket>;
  activeReason: RefusalReason | null;
  attempt: number;
  retryTimer: unknown;
  settleTimer: unknown;
  exhausted: boolean;
  /**
   * When the pending attempt is due, so a pause/resume can restore the WAIT
   * rather than restarting it. 0 when no attempt is scheduled.
   */
  nextAttemptAt: number;
};


export function createSocketRecovery(deps?: Partial<Deps>) {
  const setTimer = deps?.setTimer ?? ((cb, ms) => setTimeout(cb, ms));
  const clearTimer = deps?.clearTimer ?? ((h) => clearTimeout(h as never));
  const random = deps?.random ?? Math.random;
  const now = deps?.now ?? Date.now;

  /** Refcounts, because the same instance may be registered by successive
   * owners (a reused socket across screen remounts) whose mount/cleanup
   * ordering React does not guarantee. */
  const registrations = new Map<RecoverableSocket, number>();
  /** Sockets whose LAST disconnect was a server rejection with no refusal —
   * an auth failure. The rearm kicks must skip these: retrying a bad token
   * every 30 s of HTTP traffic is the unbounded loop "never retried" was
   * supposed to rule out. A successful connect clears the mark; a token
   * change rebuilds the socket outright. Transport-style disconnects
   * ("transport close", "ping timeout") never mark — those are exactly the
   * stranded sockets the kicks exist for. */
  const serverRejected = new WeakSet<RecoverableSocket>();
  /** Listeners are attached ONCE per instance and gated on live membership —
   * re-registering must not stack duplicate handlers. */
  const wired = new WeakSet<RecoverableSocket>();
  const domains = new Map<object, Domain>();

  let paused = false;
  let lastNudgeAt = 0;

  const keyOf = (socket: RecoverableSocket): object => socket.io ?? socket;

  /** Half-to-full jitter: [delay/2, delay]. Never zero — a zero delay is the
   * synchronized retry this exists to prevent. */
  const jittered = (ms: number) => ms * (0.5 + 0.5 * random());

  const scheduleOf = (reason: RefusalReason): readonly number[] =>
    reason === "refused_per_user_cap"
      ? PER_USER_RETRY_DELAYS_MS
      : PRESSURE_RETRY_DELAYS_MS;

  const clearRetry = (d: Domain) => {
    if (d.retryTimer !== null) clearTimer(d.retryTimer);
    d.retryTimer = null;
  };

  const clearSettle = (d: Domain) => {
    if (d.settleTimer !== null) clearTimer(d.settleTimer);
    d.settleTimer = null;
  };

  const resetDomain = (d: Domain) => {
    clearRetry(d);
    clearSettle(d);
    d.activeReason = null;
    d.attempt = 0;
    d.exhausted = false;
    d.nextAttemptAt = 0;
  };

  const allConnected = (d: Domain) => {
    if (d.sockets.size === 0) return false;
    for (const socket of d.sockets) if (socket.disconnected) return false;
    return true;
  };

  /** Success is a refusal-free settle with the domain's FULL set connected —
   * never the first `connect`, which precedes the refusal in every cycle (see
   * header). Checked again at expiry: a transport drop mid-settle must not
   * reset. */
  const beginSettle = (d: Domain) => {
    clearSettle(d);
    if (paused) return;
    d.settleTimer = setTimer(() => {
      d.settleTimer = null;
      if (allConnected(d)) {
        resetDomain(d);
        return;
      }
    }, RECOVERY_SETTLE_MS);
  };

  /** Arm the retry timer for an EXACT delay, recording when it is due. */
  const armAttempt = (d: Domain, delayMs: number) => {
    clearRetry(d);
    const delay = Math.max(0, delayMs);

    // The deadline is recorded BEFORE the pause guard, and that order is the
    // whole point.
    //
    // A refusal can land while the app is backgrounded: the disconnect handler
    // still runs, `beginOrContinueRecovery` still spends an attempt, and this
    // is still called — it just cannot arm a timer. Recording the deadline
    // after the guard left `nextAttemptAt` holding the PREVIOUS attempt's
    // deadline, already in the past, so `resume()` computed a negative
    // remainder, clamped it to zero and retried instantly. That is the very
    // collapse this function exists to prevent, arrived at from the other
    // side — and worse than the ~2 s the old code gave that case.
    d.nextAttemptAt = now() + delay;

    if (paused) return; // resume() re-arms with whatever is left of it

    d.retryTimer = setTimer(() => {
      d.retryTimer = null;
      for (const socket of d.sockets) {
        if (socket.disconnected) socket.connect();
      }
    }, delay);
  };

  const scheduleAttempt = (d: Domain, delayMs: number) =>
    armAttempt(d, jittered(delayMs));

  /**
   * Put back the wait a background pause cancelled, instead of starting a new
   * short one.
   *
   * `pause()` clears the retry timer, so `resume()` used to see a null timer
   * and re-arm at RESUME_RETRY_DELAY_MS (~2 s). Measured on an Android device,
   * 2026-09-05: a correctly-scheduled 231,659 ms backoff was replaced 5 s later
   * by 1,722 ms, the attempt fired, and a two-attempt budget was spent in SEVEN
   * SECONDS instead of about four minutes.
   *
   * For a per-user cap that is merely untidy. For `refused_task_pressure` it
   * inverts the whole point: the server is shedding BECAUSE it is overloaded,
   * and every user who switches apps would retry within two seconds — during a
   * load event, when people switch apps constantly. The bounded schedule would
   * become a retry storm paced by user behaviour, against a server that had
   * already said stop.
   *
   * Time spent backgrounded counts toward the wait, which is why this is a
   * deadline rather than a remaining-duration: five minutes in the background
   * during a 231 s backoff SHOULD retry immediately on return.
   */
  const rearmRemaining = (d: Domain) =>
    armAttempt(d, d.nextAttemptAt - now());

  const beginOrContinueRecovery = (d: Domain, reason: RefusalReason) => {
    clearSettle(d); // a refusal voids any success-in-progress
    d.activeReason = reason;
    const schedule = scheduleOf(reason);
    if (d.attempt >= schedule.length) {
      // Bounded: park until a lifecycle trigger (resume / token change).
      clearRetry(d);
      d.exhausted = true;
      return;
    }
    const delay = schedule[d.attempt];
    d.attempt += 1;
    scheduleAttempt(d, delay);
  };

  /** Reconnect transport-dead sockets in domains with no refusal in play.
   * Refusal domains are deliberately untouched — their bounded schedule owns
   * the timing, and a kick here would defeat the backoff. Auth-rejected
   * sockets are skipped for the same rule from the other side: the kicks are
   * lifecycle-unbounded, so they may only reach sockets whose failure was
   * the network's, never ones the server has already turned away. */
  const kickIdleDomains = () => {
    for (const d of domains.values()) {
      if (d.activeReason !== null) continue;
      for (const socket of d.sockets) {
        if (socket.disconnected && !serverRejected.has(socket)) {
          socket.connect();
        }
      }
    }
  };

  return {
    /**
     * Adopt a socket: watch it for refusals, recover it, drop it when its
     * owner tears it down. Returns the unregister function (idempotent).
     */
    register(socket: RecoverableSocket): () => void {
      registrations.set(socket, (registrations.get(socket) ?? 0) + 1);
      const key = keyOf(socket);
      let domain = domains.get(key);
      if (!domain) {
        domain = {
          sockets: new Set(),
          activeReason: null,
          attempt: 0,
          retryTimer: null,
          settleTimer: null,
          exhausted: false,
          nextAttemptAt: 0,
        };
        domains.set(key, domain);
      }
      domain.sockets.add(socket);

      if (!wired.has(socket)) {
        wired.add(socket);
        // PER-SOCKET, deliberately: the refusal event and the disconnect it
        // classifies always belong to the SAME namespace socket, but sibling
        // packets interleave on one transport — a sibling's connect ack
        // arriving between this socket's refusal and its disconnect must not
        // erase the classification.
        let pendingReason: RefusalReason | null = null;
        const liveDomain = (): Domain | undefined => {
          const d = domains.get(keyOf(socket));
          return d?.sockets.has(socket) ? d : undefined;
        };
        socket.on(REFUSED_EVENT, (...args: unknown[]) => {
          if (!liveDomain()) return;
          const reason = (args[0] as { reason?: string } | undefined)?.reason;
          pendingReason =
            reason === "refused_per_user_cap"
              ? reason
              : "refused_task_pressure";
        });
        socket.on("disconnect", (...args: unknown[]) => {
          const d = liveDomain();
          if (!d) return;
          if (args[0] !== "io server disconnect") return;
          if (pendingReason === null) {
            // Auth failure etc. — never retried, by ANY path: no schedule
            // here, and the mark keeps the lifecycle kicks away too.
            serverRejected.add(socket);
            return;
          }
          const refusal = pendingReason;
          pendingReason = null;
          beginOrContinueRecovery(d, refusal);
        });
        socket.on("connect", () => {
          serverRejected.delete(socket);
          // NOT success yet — in a refusal cycle the connect ack precedes the
          // refusal. One socket connecting also proves nothing while a
          // sibling in the same domain can still be refused (partial
          // acceptance): only the full set connected, held through a
          // refusal-free settle, resets.
          pendingReason = null;
          const d = liveDomain();
          if (d && d.activeReason !== null && allConnected(d)) beginSettle(d);
        });
      }

      let done = false;
      return () => {
        if (done) return;
        done = true;
        const count = (registrations.get(socket) ?? 1) - 1;
        if (count > 0) {
          registrations.set(socket, count);
          return;
        }
        registrations.delete(socket);
        const d = domains.get(key);
        if (!d) return;
        d.sockets.delete(socket);
        if (d.sockets.size === 0) {
          // The last owner tearing down (token change, logout) cancels this
          // domain outright: there is nothing left to reconnect, and rebuilt
          // sockets register themselves fresh.
          resetDomain(d);
          domains.delete(key);
          return;
        }
        // Membership changing can BE the recovery: if the departed socket was
        // the last disconnected one, the survivors are the whole set now, and
        // without this re-check recovery would sit "in progress" forever with
        // no timer, bequeathing its half-spent attempt budget to the next
        // unrelated refusal.
        if (d.activeReason !== null && allConnected(d)) beginSettle(d);
      };
    },

    /** Background: hold every timer. A suspended app cannot usefully retry. */
    pause(): void {
      paused = true;
      for (const d of domains.values()) {
        clearRetry(d);
        clearSettle(d);
      }
    },

    /**
     * Foreground: the one lifecycle moment that must restart recovery, because
     * foregrounding only triggers an HTTP resync and never recreates the
     * sockets. Each exhausted domain gets ONE fresh attempt — bounded per
     * trigger, not per refusal — and idle domains get their transport-dead
     * sockets kicked (socket.io's finite budget may have run out; the Manager
     * resets its backoff when it gives up, so one connect() grants a fresh
     * bounded cycle — verified in manager.js).
     */
    resume(): void {
      paused = false;
      kickIdleDomains();
      for (const d of domains.values()) {
        if (d.activeReason === null) continue;
        if (allConnected(d)) {
          // The set survived the background pause connected — resume the
          // success settle rather than reconnecting sockets that are up.
          beginSettle(d);
          continue;
        }
        if (d.exhausted) {
          d.exhausted = false;
          // One attempt; a refusal of it re-enters the (already spent)
          // schedule and parks again.
          scheduleAttempt(d, RESUME_RETRY_DELAY_MS);
          continue;
        }
        if (d.retryTimer === null) {
          // A schedule interrupted by the pause resumes where it was; only a
          // domain with no pending attempt gets the short resume retry.
          if (d.nextAttemptAt > 0) rearmRemaining(d);
          else scheduleAttempt(d, RESUME_RETRY_DELAY_MS);
        }
      }
    },

    /**
     * A successful HTTP response proved the network works. If a socket is
     * still transport-dead (socket.io's finite budget ran out mid-outage and
     * the app never left the foreground), this is the only signal that
     * arrives, so it rearms — throttled, refusal domains untouched.
     */
    nudge(): void {
      if (paused) return;
      const t = now();
      if (t - lastNudgeAt < NUDGE_MIN_INTERVAL_MS) return;
      lastNudgeAt = t;
      kickIdleDomains();
    },

    /** Tests and diagnostics only. Aggregates across domains. */
    _state(): { attempt: number; exhausted: boolean; recovering: boolean; size: number } {
      let attempt = 0;
      let exhausted = false;
      let recovering = false;
      for (const d of domains.values()) {
        attempt = Math.max(attempt, d.attempt);
        exhausted = exhausted || d.exhausted;
        recovering = recovering || d.activeReason !== null;
      }
      return { attempt, exhausted, recovering, size: registrations.size };
    },
  };
}

/** The app-wide coordinator every socket registers with. */
export const socketRecovery = createSocketRecovery();
